import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { X, Clock, Sparkles, AlertTriangle } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { Skeleton } from "@/components/ui/skeleton";

export const TrialBanner = () => {
  const [isDismissed, setIsDismissed] = useState(false);
  const navigate = useNavigate();
  const { subscription, loading, hasAccess, daysRemaining } = useSubscription();

  if (loading) {
    return <Skeleton className="h-12 w-full" />;
  }

  // Don't show if dismissed, no subscription, or active paid subscription
  if (isDismissed || !subscription || subscription.status === 'active') {
    return null;
  }

  // Only show for trial users with access
  if (subscription.status !== 'trial' || !hasAccess || daysRemaining === null) {
    return null;
  }

  const isUrgent = daysRemaining <= 3;
  const isVeryUrgent = daysRemaining <= 1;

  // Choose variant based on urgency
  if (isVeryUrgent) {
    return (
      <Alert className="border-destructive/50 bg-destructive/5 rounded-none border-x-0 border-t-0">
        <AlertTriangle className="h-4 w-4 text-destructive" />
        <AlertDescription className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <strong className="text-destructive">
              {daysRemaining === 0 ? "Last day of your trial!" : "1 day left in your trial!"}
            </strong>
            <span className="text-muted-foreground hidden sm:inline">
              Subscribe now to keep access to all features.
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button 
              size="sm" 
              variant="destructive"
              onClick={() => navigate("/checkout")}
            >
              Subscribe Now
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() => setIsDismissed(true)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  if (isUrgent) {
    return (
      <Alert className="border-warning/50 bg-warning/5 rounded-none border-x-0 border-t-0">
        <Clock className="h-4 w-4 text-warning" />
        <AlertDescription className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <strong className="text-warning-foreground">
              {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left in your trial
            </strong>
            <span className="text-muted-foreground hidden sm:inline">
              Upgrade to continue using ClassPlan.
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button 
              size="sm" 
              onClick={() => navigate("/checkout")}
            >
              Upgrade Now
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0"
              onClick={() => setIsDismissed(true)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Normal trial banner (> 3 days remaining)
  return (
    <Alert className="border-primary/30 bg-primary/5 rounded-none border-x-0 border-t-0">
      <Sparkles className="h-4 w-4 text-primary" />
      <AlertDescription className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <strong className="text-foreground">
            {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left in your free trial
          </strong>
          <span className="text-muted-foreground hidden sm:inline">
            Enjoying ClassPlan? Upgrade anytime.
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => navigate("/checkout")}
          >
            View Plans
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={() => setIsDismissed(true)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
};
